import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Select, Empty, Badge } from "@/components/ui-kit";
import { RotateCcw, ScrollText } from "lucide-react";

const ROLE_LABEL = { admin: "Administrateur", coach: "Coach", player: "Joueur", parent: "Parent", system: "Système" };
const ROLE_COLOR = { admin: "primary", coach: "blue", player: "green", parent: "yellow", system: "gray" };
const ENTITIES = ["membre", "catégorie", "événement", "actualité", "exercice", "droits d'accès",
  "club", "paramètres", "code d'accès", "rappel", "paiement", "présence", "compétence", "cotisation"];

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [entity, setEntity] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (entity) params.entity_type = entity;
    if (role) params.user_role = role;
    api.get("/audit-logs", { params }).then((r) => setLogs(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, [entity, role]);

  return (
    <div data-testid="audit-page">
      <PageHeader title="Journal d'audit" subtitle="Historique des actions sensibles : qui a fait quoi, et quand."
        action={<Button data-testid="refresh-audit-btn" variant="outline" onClick={load}><RotateCcw size={16} /> Actualiser</Button>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="w-56">
          <Select data-testid="filter-entity" value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">Toutes les catégories</option>
            {ENTITIES.map((x) => <option key={x} value={x}>{x}</option>)}
          </Select>
        </div>
        <div className="w-48">
          <Select data-testid="filter-role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">Tous les rôles</option>
            {Object.keys(ROLE_LABEL).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </Select>
        </div>
      </div>

      {loading ? <Empty text="Chargement..." /> : logs.length === 0 ? (
        <Empty text="Aucune action enregistrée pour ces filtres." />
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-4 py-3 font-semibold">Quand</th>
                  <th className="px-4 py-3 font-semibold">Utilisateur</th>
                  <th className="px-4 py-3 font-semibold">Rôle</th>
                  <th className="px-4 py-3 font-semibold">Action</th>
                  <th className="px-4 py-3 font-semibold">Cible</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} data-testid="audit-row" className="border-b border-border/60 hover:bg-secondary/30">
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmt(l.at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{l.user_name || "—"}</div>
                      {l.user_email && <div className="text-xs text-muted-foreground">{l.user_email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={ROLE_COLOR[l.user_role] || "gray"}>{ROLE_LABEL[l.user_role] || l.user_role || "—"}</Badge>
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {l.action}
                      {l.meta && l.meta.fee_label ? <span className="text-xs text-muted-foreground"> · {l.meta.fee_label}{l.meta.amount ? ` (${l.meta.amount} €)` : ""}</span> : null}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-muted-foreground">{l.entity_type || "—"}</span>
                      {l.entity_id ? <span className="text-xs text-muted-foreground/70"> #{String(l.entity_id).slice(-6)}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-border/60">
            {logs.map((l) => (
              <div key={l.id} data-testid="audit-card" className="p-4">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-semibold text-sm">{l.action}</span>
                  <Badge color={ROLE_COLOR[l.user_role] || "gray"}>{ROLE_LABEL[l.user_role] || l.user_role || "—"}</Badge>
                </div>
                {l.meta && l.meta.fee_label ? <p className="text-xs text-muted-foreground mb-1">{l.meta.fee_label}{l.meta.amount ? ` (${l.meta.amount} €)` : ""}</p> : null}
                <p className="text-xs text-muted-foreground">{l.user_name || "—"}{l.user_email ? ` · ${l.user_email}` : ""}</p>
                <div className="flex items-center justify-between gap-2 mt-1.5 text-xs text-muted-foreground">
                  <span>{l.entity_type || "—"}{l.entity_id ? ` #${String(l.entity_id).slice(-6)}` : ""}</span>
                  <span className="whitespace-nowrap">{fmt(l.at)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
